/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-student
 * @responsibility Compose independently tuned desktop and mobile SET-F10 student settings pane
 *   surfaces: display name, avatar (read + clear only, no upload), language, and -- org-login
 *   students only -- the password-change form. Purpose-built for the student actor, not a reuse
 *   of SET-F02's AccountSettingsSurface (which bakes in email/linkedProviders concepts that do
 *   not apply to organization-session students).
 * @state-owner React transient edit/pending form state; parent owns server state
 * @allowed-imports React; shared translator; @vispo/contracts types
 * @forbidden direct API client; email or provider-linking UI or copy
 * @public-api StudentSettingsSurface
 * @side-effects invokes parent commands only
 * @tests StudentSettingsSurface.test.tsx
 */

import type { ProfileContext } from '@vispo/contracts';
import { useState } from 'react';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-student.css';

const LANGUAGE_OPTIONS: ReadonlyArray<{ label: string; value: 'zh' | 'en' }> = [
  { label: '繁體中文', value: 'zh' },
  { label: 'English', value: 'en' },
];

function firstGrapheme(value: string): string {
  return Array.from(value.trim())[0]?.toLocaleUpperCase() || 'V';
}

export interface StudentSettingsSurfaceProps {
  avatarUrl: string | null;
  locale: 'zh' | 'en';
  mobile: boolean;
  onClearAvatar(): void;
  onRetry(): void;
  onSaveName(displayName: string): void;
  onSelectLocale(locale: 'zh' | 'en'): void;
  profile: ProfileContext;
  profileErrorCode: string | null;
  preferencesErrorCode: string | null;
  profileSaving: boolean;
  preferencesSaving: boolean;
  /** null for general students -- the password section is entirely absent for them, not disabled. */
  password: {
    errorCode: string | null;
    onChangePassword(currentPassword: string, newPassword: string): void;
    onResetError(): void;
    saving: boolean;
    success: boolean;
  } | null;
}

function passwordErrorMessage(
  t: ReturnType<typeof useTranslator>,
  errorCode: string | null,
): string | null {
  if (!errorCode) return null;
  // Non-disclosure: wrong current password and an expired/invalid session are the identical
  // ORG_AUTH_INVALID code from the backend -- this UI must not try to tell them apart either.
  if (errorCode === 'ORG_AUTH_INVALID') return t('目前密碼不正確，或需要重新登入。');
  if (errorCode === 'ORG_AUTH_PASSWORD_REJECTED') return t('新密碼不符合規則，請換一組再試。');
  return t('更新失敗，請重試');
}

function PasswordSection({
  password,
}: {
  password: NonNullable<StudentSettingsSurfaceProps['password']>;
}) {
  const t = useTranslator();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const canSave = currentPassword.length > 0 && newPassword.length >= 8 && !password.saving;

  const submit = () => {
    password.onChangePassword(currentPassword, newPassword);
  };

  return (
    <div className="student-settings-pane__section">
      <span className="student-settings-pane__label">{t('重設密碼')}</span>
      {password.success ? (
        <p className="student-settings-pane__success" role="status">
          {t('密碼已更新')}
        </p>
      ) : null}
      {password.errorCode ? (
        <div className="student-settings-pane__error" role="alert">
          <span>{passwordErrorMessage(t, password.errorCode)}</span>
        </div>
      ) : null}
      <label className="student-settings-pane__field">
        <span>{t('目前密碼')}</span>
        <input
          autoComplete="current-password"
          disabled={password.saving}
          type="password"
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            password.onResetError();
          }}
        />
      </label>
      <label className="student-settings-pane__field">
        <span>{t('新密碼')}</span>
        <input
          autoComplete="new-password"
          disabled={password.saving}
          minLength={8}
          type="password"
          value={newPassword}
          onChange={(event) => {
            setNewPassword(event.target.value);
            password.onResetError();
          }}
        />
      </label>
      <button
        className="student-settings-pane__primary"
        disabled={!canSave}
        type="button"
        onClick={submit}
      >
        {password.saving ? t('儲存中…') : t('更新密碼')}
      </button>
    </div>
  );
}

export function StudentSettingsSurface(props: StudentSettingsSurfaceProps) {
  const t = useTranslator();
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(props.profile.display_name);

  const startEdit = () => {
    setDraftName(props.profile.display_name);
    setEditingName(true);
  };
  const cancelEdit = () => setEditingName(false);
  const save = () => {
    props.onSaveName(draftName);
    setEditingName(false);
  };
  const trimmedDraft = draftName.trim();
  const isDirty = trimmedDraft !== props.profile.display_name;
  const isValid = trimmedDraft.length >= 1 && trimmedDraft.length <= 120;
  const canSaveName = isDirty && isValid;

  return (
    <section
      aria-label={t('設定')}
      className={`student-settings-pane${props.mobile ? ' student-settings-pane--mobile' : ' student-settings-pane--desktop'}`}
    >
      {props.profileErrorCode ? (
        <div className="student-settings-pane__error" role="alert">
          <span>
            {props.profileErrorCode === 'ACCOUNT_REVISION_CONFLICT'
              ? t('已被他人修改')
              : t('更新失敗，請重試')}
          </span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : null}

      <div className="student-settings-pane__identity">
        <span className="student-settings-pane__avatar">
          {props.avatarUrl ? (
            <img alt="" src={props.avatarUrl} />
          ) : (
            firstGrapheme(props.profile.display_name)
          )}
        </span>
        <div className="student-settings-pane__name">
          {editingName ? (
            <>
              <input
                aria-label={t('姓名')}
                disabled={props.profileSaving}
                maxLength={120}
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
              />
              <div className="student-settings-pane__actions">
                <button disabled={props.profileSaving} type="button" onClick={cancelEdit}>
                  {t('取消')}
                </button>
                <button
                  className="student-settings-pane__primary"
                  disabled={!canSaveName || props.profileSaving}
                  type="button"
                  onClick={save}
                >
                  {props.profileSaving ? t('儲存中…') : t('儲存')}
                </button>
              </div>
            </>
          ) : (
            <>
              <b>{props.profile.display_name}</b>
              <button type="button" onClick={startEdit}>
                {t('編輯')}
              </button>
            </>
          )}
        </div>
      </div>

      {props.avatarUrl ? (
        <button
          className="student-settings-pane__clear-avatar"
          disabled={props.profileSaving}
          type="button"
          onClick={props.onClearAvatar}
        >
          {t('移除頭像')}
        </button>
      ) : null}

      <div className="student-settings-pane__section">
        <span className="student-settings-pane__label" id="student-settings-pane-language-label">
          {t('介面語言')}
        </span>
        {props.preferencesErrorCode ? (
          <div className="student-settings-pane__error" role="alert">
            <span>{t('更新失敗，請重試')}</span>
          </div>
        ) : null}
        <div
          aria-labelledby="student-settings-pane-language-label"
          className="student-settings-pane__language"
          role="radiogroup"
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <label
              className={`student-settings-pane__language-option${
                option.value === props.locale ? ' is-selected' : ''
              }`}
              key={option.value}
            >
              <input
                checked={option.value === props.locale}
                disabled={props.preferencesSaving}
                name="student-settings-pane-language"
                type="radio"
                value={option.value}
                onChange={() => {
                  if (option.value !== props.locale) props.onSelectLocale(option.value);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {props.password ? <PasswordSection password={props.password} /> : null}
    </section>
  );
}
